import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import ApplicationPreview from "../CfcForm/ApplicationPreview";
import FieldInspectionForm from "../../../components/district_admin/FieldInspectionForm";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import api from "@/utils/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { CheckCircle, Clock, Download, FileText } from "lucide-react";
import ProvisionalSanctionForm from "../../../components/district_admin/ProvisionalSanctionForm";
import StatusBadge from "../../../components/common/StatusBadge";

const CFCSubsidyDetails = ({ application }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-IN");
  };
  const [data, setData] = useState();
  const role = useSelector((state) => state.auth.user?.role) || "applicant";
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [adminApproveModalOpen, setAdminApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [approveFile, setApproveFile] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [provisionalSanctionAmount, setProvisionalSanctionAmount] = useState(
    application?.sanction?.sanction_order_amount
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const uploads_array = [
    { name: "Profile Photo", value: "photo" },
    { name: "Basic Cluster Data Copy", value: "basic_cluster_data_copy" },
    { name: "Pan Copy", value: "pan_copy" },
    { name: "TAN Copy", value: "tan_copy" },
    { name: "GST Copy", value: "gst_copy" },
    { name: "Registration Copy", value: "registration_copy" },
    { name: "Members Aadhaar Copy", value: "members_aadhaar_copy" },
    { name: "Bye Laws Copy", value: "bye_laws_copy" },
    { name: "Commitment Letter", value: "commitment_letter" },
    {
      name: "Capacity Utilization Evidence",
      value: "capacity_utilization_evidence",
    },
    { name: "Export Oriented Copy", value: "export_oriented_copy" },
    { name: "Other", value: "other" },
  ];

  const tabsOrder = [
    { id: "spv", name: "SPV Information" },
    { id: "eligibility", name: "Beneficiary Details" },
    { id: "bank", name: "Bank Details" },
    { id: "priority", name: "Additional Details" },
    { id: "declaration", name: "Declaration" },
    { id: "all_documents", name: "All Uploaded Documents" },
    {
      id: "m_panel_list",
      name: "Verified with M-Panel supplier List's (13 supplier from G.O.I and List Approved by D.o.H",
    },
  ];

  useEffect(() => {
    setData({ ...application });
  }, [application]);

  const openApproveModal = () => {
    if (role == "admin") {
      setAdminApproveModalOpen(true);
    } else {
      setApproveModalOpen(true);
    }
  };

  const openRejectModal = () => {
    setRejectModalOpen(true);
  };

  const handleApprove = async () => {
    try {
      setIsSubmitting(true);
      let response;

      if (role == "admin") {
        const formData = new FormData();
        formData.append("id", data?.id);
        formData.append("remarks", remarks);
        formData.append("sanction_order_amount", provisionalSanctionAmount);

        if (approveFile) {
          formData.append("approve_file", approveFile);
        }

        response = await api.post("cfc-subsidies/committee/approve", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        response = await api.post("cfc-subsidies/district-admin/approve", {
          id: data?.id,
          remarks: remarks,
        });
      }

      setData(response?.data?.data);
      toast({
        title: "Approved",
        description: "Submission approved successfully",
        variant: "success",
      });
      setApproveModalOpen(false);
      setAdminApproveModalOpen(false);
      setRemarks("");
      setProvisionalSanctionAmount("");
    } catch (error) {
      toast({
        title: "Failed",
        description: error.response?.data?.message || "Approval failed",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    try {
      setIsSubmitting(true);
      const submitData = new FormData();
      submitData.append("remarks", remarks);
      submitData.append("form_id", data?.id);
      let response;

      if (role == "admin") {
        response = await api.post(
          "cfc-subsidies/committee/reject",
          submitData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      } else {
        response = await api.post(
          "cfc-subsidies/district-admin/reject",
          submitData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      }
      setData(response?.data?.data);
      toast({
        title: "Rejected",
        description: "Submission rejected successfully",
        variant: "destructive",
      });
      setRejectModalOpen(false);
      setRemarks("");
    } catch (error) {
      toast({
        title: "Failed",
        description: error.response?.data?.message || "Rejection failed",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldVisitRemark = data?.remarks?.find(
    (remark) => remark.status === "field_visit_completed"
  );

  const districtOfficerApprove = data?.remarks?.find(
    (remark) => remark.status === "district_officer_approve"
  );

  const districtOfficerReject = data?.remarks?.find(
    (remark) => remark.status === "district_officer_reject"
  );

  const committeeApprove = data?.remarks?.find(
    (remark) => remark.status === "committee_approve"
  );

  const committeeReject = data?.remarks?.find(
    (remark) => remark.status === "committee_reject"
  );

  const handleDownload = (fileUrl) => {
    if (!fileUrl) return;
    window.open(fileUrl, "_blank");

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const openInNewTab = (fileUrl) => {
    if (!fileUrl) return;
    window.open(fileUrl, "_blank");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">CFC Subsidy Application Details</h1>
        <div className="flex gap-2">
          <Button asChild variant="default">
            <Link to="/submissions">Back</Link>
          </Button>
        </div>
      </div>
      <div className="flex items-center justify-end">
        <div className="flex gap-2">
          {data?.status == "field_visit_completed" && (
            <Button
              variant="default"
              onClick={() => handleDownload(data?.provisional_sanction_letter)}
            >
              Download Provisional Sanction Letter
            </Button>
          )}
          <Button
            variant="default"
            onClick={() => handleDownload(data?.application_url)}
          >
            Download Application
          </Button>
          <Button
            variant="default"
            onClick={() => handleDownload(data?.application_uploads)}
          >
            Download Application Attachments
          </Button>
          {role == "district_admin" &&
            data?.status != "district_officer_approve" &&
            data?.status != "district_officer_reject" &&
            data?.status != "committee_approve" &&
            data?.status != "committee_reject" && (
              <Button variant="destructive" onClick={() => openRejectModal()}>
                Reject Application
              </Button>
            )}
        </div>
      </div>

      <Card className="my-5 bg-white">
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            <span>Common Facility Centre Application</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-5">
            <p>
              <strong>Application Number:</strong> {data?.application_number}
            </p>
            <p>
              <strong>Status:</strong> <StatusBadge status={data?.status} />
            </p>
          </div>
        </CardContent>
      </Card>

      <ApplicationPreview application={data} viewPage={true} />

      {(role === "district_admin" || role == "admin") && (
        <>
          <Card className="bg-white p-3">
            <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
              <CardTitle>District Officer Field Inspection</CardTitle>
            </CardHeader>
            <CardContent>
              {data?.status &&
              data?.status === "submitted_for_provisional_sanction" &&
              role == "district_admin" ? (
                <FieldInspectionForm
                  formType="cfc-subsidies"
                  tabsOrder={tabsOrder}
                  form_id={data?.id}
                  setData={setData}
                  uploads_array={uploads_array}
                  application={data}
                />
              ) : fieldVisitRemark ? (
                <div className="space-y-6 mt-4">
                  {/* Summary */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <h3 className="font-semibold text-green-900">
                        Field Inspection Completed
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600 mb-3">Inspected By</p>
                        <p className="font-medium">
                          {fieldVisitRemark.user_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-3">Inspection Date</p>
                        <p className="font-medium">
                          {new Date(
                            fieldVisitRemark.remarks_date
                          ).toLocaleDateString("en-IN", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-3">GPS Location</p>
                        <a
                          href={fieldVisitRemark?.geo_tag}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Tab Checklist */}
                  {fieldVisitRemark.tabs &&
                    fieldVisitRemark.tabs.length > 0 && (
                      <div className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-semibold">
                            Tab Verification Status
                          </h4>
                          <span className="text-sm text-gray-600">
                            {
                              fieldVisitRemark.tabs.filter(
                                (t) => t.status === 1
                              ).length
                            }{" "}
                            / {fieldVisitRemark.tabs.length} Completed
                          </span>
                        </div>
                        <div className="space-y-2">
                          {fieldVisitRemark.tabs.map((tab) => {
                            const tabName =
                              tabsOrder?.find((t) => t.id === tab.tab_name)
                                ?.name || tab.tab_name.toUpperCase();
                            const isCompleted = tab.status === 1;
                            return (
                              <div
                                key={tab.tab_name}
                                className={`flex items-center justify-between p-3 border rounded ${
                                  isCompleted
                                    ? "bg-green-50 border-green-200"
                                    : "bg-gray-50 border-gray-200"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {isCompleted ? (
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                  ) : (
                                    <Clock className="h-4 w-4 text-gray-400" />
                                  )}
                                  <span
                                    className={`text-sm font-medium ${
                                      isCompleted
                                        ? "text-green-800"
                                        : "text-gray-600"
                                    }`}
                                  >
                                    {tabName}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-600">
                                  {isCompleted ? "Verified" : "Pending"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  {/* Inspection Remarks */}
                  {fieldVisitRemark.description && (
                    <div className="border rounded-lg p-4">
                      <h4 className="font-semibold mb-2">Inspection Remarks</h4>
                      <div className="bg-gray-50 p-3 rounded border">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {fieldVisitRemark.description}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Uploaded Documents */}
                  {fieldVisitRemark.files &&
                    fieldVisitRemark.files.length > 0 && (
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3">
                          Uploaded Documents
                        </h4>
                        <div className="space-y-2">
                          {fieldVisitRemark.files.map((file, index) => (
                            <div
                              key={file.id}
                              className="flex items-center justify-between p-3 border rounded hover:bg-gray-50"
                            >
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-blue-600" />
                                <span className="text-sm font-medium">
                                  {file.upload_type || `Document ${index + 1}`}
                                </span>
                              </div>
                              <a
                                href={file.upload_copy}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                              >
                                <Download className="h-4 w-4" />
                                Download
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              ) : (
                <p className="text-gray-500">
                  No field inspection record found
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white p-3">
            <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
              <CardTitle>Provisional Sanction</CardTitle>
              {data?.status == "field_visit_completed" && (
                <Button
                  variant="default"
                  onClick={() =>
                    handleDownload(data?.provisional_sanction_letter)
                  }
                >
                  Download Provisional Sanction Letter
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {data?.status &&
              data?.status == "field_visit_completed" &&
              role == "district_admin" ? (
                <ProvisionalSanctionForm
                  formType="cfc-subsidies"
                  form_id={data?.id}
                  setData={setData}
                  application={data}
                />
              ) : data?.sanction ? (
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Provisional Sanction Order No
                      </label>
                      <p className="text-base font-semibold">
                        {data.sanction.sanction_order_no}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Provisional Sanction Order Date
                      </label>
                      <p className="text-base font-semibold">
                        {new Date(
                          data.sanction.sanction_order_date
                        ).toLocaleDateString()}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Provisional Sanction Amount
                      </label>
                      <p className="text-base font-semibold">
                        ₹
                        {data.sanction.sanction_order_amount}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        District Officer
                      </label>
                      <p className="text-base font-semibold">
                        {data.sanction.district_officer?.name}
                      </p>
                    </div>
                  </div>

                  {data.sanction.description && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Description
                      </label>
                      <p className="text-base">{data.sanction.description}</p>
                    </div>
                  )}

                  {data.sanction.sanctioned_copy && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Provisional Sanctioned Copy
                      </label>
                      <div className="mt-2">
                        <a
                          href={data.sanction.sanctioned_copy}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                          <svg
                            className="w-4 h-4 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          Download Sanction Copy
                        </a>
                      </div>
                    </div>
                  )}

                  {data.sanction.is_user_accept !== null && (
                    <div className="pt-4 border-t">
                      <label className="text-sm font-medium text-gray-500">
                        User Acceptance Status
                      </label>
                      <p className="text-base font-semibold">
                        {data.sanction.is_user_accept ? (
                          <span className="text-green-600">Accepted</span>
                        ) : (
                          <span className="text-red-600">Not Accepted</span>
                        )}
                      </p>
                      {data.sanction.user_accept_date && (
                        <p className="text-sm text-gray-500 mt-1">
                          on{" "}
                          {new Date(data?.sanction.user_accept_date)
                            .toLocaleDateString("en-GB")
                            .replace(/\//g, " - ")}
                          {"  "}
                          {new Date(
                            data?.sanction.user_accept_date
                          ).toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">No sanction details available</p>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {districtOfficerApprove && (
        <Card className="bg-white p-3">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle>District Officer Approval</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 mt-4">
              {/* Summary */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h3 className="font-semibold text-green-900">
                    Approval Completed
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Approved By</p>
                    <p className="font-medium">
                      {districtOfficerApprove.user_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Approval Date</p>
                    <p className="font-medium">
                      {new Date(districtOfficerApprove.remarks_date)
                        .toLocaleDateString("en-GB")
                        .replace(/\//g, " - ")}
                      {"  "}
                      {new Date(
                        districtOfficerApprove.remarks_date
                      ).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Approval Remarks */}
              {districtOfficerApprove.description && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Approval Remarks</h4>
                  <div className="bg-gray-50 p-3 rounded border">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {districtOfficerApprove.description}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {districtOfficerReject && (
        <Card className="bg-white p-3">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle>District Officer Reject</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 mt-4">
              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-900">
                    Application Rejected
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Rejected By</p>
                    <p className="font-medium">
                      {districtOfficerReject.user_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Rejected Date</p>
                    <p className="font-medium">
                      {new Date(districtOfficerReject.remarks_date)
                        .toLocaleDateString("en-GB")
                        .replace(/\//g, " - ")}
                      {"  "}
                      {new Date(
                        districtOfficerReject.remarks_date
                      ).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Approval Remarks */}
              {districtOfficerReject.description && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Rejected Remarks</h4>
                  <div className="bg-gray-50 p-3 rounded border">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {districtOfficerReject.description}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {committeeApprove && (
        <Card className="bg-white p-3">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle>Committee Approval</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 mt-4">
              {/* Summary */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h3 className="font-semibold text-green-900">
                    Approval Completed
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Approved By</p>
                    <p className="font-medium">{committeeApprove.user_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Approval Date</p>
                    <p className="font-medium">
                      {new Date(committeeApprove.remarks_date)
                        .toLocaleDateString("en-GB")
                        .replace(/\//g, " - ")}
                      {"  "}
                      {new Date(
                        committeeApprove.remarks_date
                      ).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Approval Remarks */}
              {committeeApprove.description && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Approval Remarks</h4>
                  <div className="bg-gray-50 p-3 rounded border">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {committeeApprove.description}
                    </p>
                  </div>
                </div>
              )}

              {committeeApprove.approve_file && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Approval Document</h4>
                  <div className="flex items-center justify-between p-3 border rounded hover:bg-gray-50">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium">Approval File</span>
                    </div>
                    <button
                      onClick={() =>
                        openInNewTab(committeeApprove.approve_file)
                      }
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <Download className="h-4 w-4" />
                      Download PDF
                    </button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {committeeReject && (
        <Card className="bg-white p-3">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle>Committee Reject</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 mt-4">
              {/* Summary */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h3 className="font-semibold text-green-900">
                    Application Rejected
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Rejected By</p>
                    <p className="font-medium">{committeeReject.user_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Rejected Date</p>
                    <p className="font-medium">
                      {new Date(committeeReject.remarks_date)
                        .toLocaleDateString("en-GB")
                        .replace(/\//g, " - ")}
                      {"  "}
                      {new Date(
                        committeeReject.remarks_date
                      ).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Approval Remarks */}
              {committeeReject.description && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Rejected Remarks</h4>
                  <div className="bg-gray-50 p-3 rounded border">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {committeeReject.description}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {role == "district_admin" && data?.status == "submitted_for_approval" && (
        <Card>
          <CardHeader>
            <CardTitle>District Officer Approval</CardTitle>
          </CardHeader>
          <CardContent className="flex justify-start gap-4 mt-10">
            <Button
              disabled={isSubmitting}
              variant="success"
              onClick={() => openApproveModal()}
              className="py-5"
            >
              Approve the Application and Send for Final Committee
            </Button>
            <Button
              disabled={isSubmitting}
              variant="destructive"
              onClick={() => openRejectModal()}
            >
              Reject the Application
            </Button>
          </CardContent>
        </Card>
      )}

      {role == "admin" && data?.status == "district_officer_approve" && (
        <Card className="bg-white p-3">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle>Committee Approval</CardTitle>
          </CardHeader>
          <CardContent className="flex justify-start gap-4 mt-10">
            <Button
              disabled={isSubmitting}
              variant="success"
              onClick={() => openApproveModal()}
              className="py-5"
            >
              Approve the Application
            </Button>
            <Button
              disabled={isSubmitting}
              variant="destructive"
              onClick={() => openRejectModal()}
            >
              Reject the Application
            </Button>
          </CardContent>
        </Card>
      )}

      {role == "admin" && data?.status == "committee_approve" && (
        <Card className="bg-white p-3">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle>Reject Application</CardTitle>
          </CardHeader>
          <CardContent className="flex justify-start gap-4 mt-10">
            <Button
              disabled={isSubmitting}
              variant="destructive"
              onClick={() => openRejectModal()}
            >
              Reject the Application
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Dialogs remain the same */}
      <Dialog open={approveModalOpen} onOpenChange={setApproveModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Approval</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            Are you sure you want to approve this application. Once done the
            application will be sent to the committee
          </div>
          <div className="py-4 space-y-2">
            <p>Enter remarks:</p>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter remarks..."
            />
          </div>
          <DialogFooter className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => setApproveModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={isSubmitting}>
              {isSubmitting ? "Processing..." : "Approve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Submission</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-2">
            <p>Enter remarks for rejection:</p>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter remarks..."
            />
          </div>
          <DialogFooter className="flex space-x-2">
            <Button variant="outline" onClick={() => setRejectModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={isSubmitting || !remarks.trim()}
            >
              {isSubmitting ? "Processing..." : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={adminApproveModalOpen}
        onOpenChange={setAdminApproveModalOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Submission</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            Are you sure you want to approve this Application. Once done the
            Application will be freezed and a sms will be sent to the concern
            Applicant.
          </div>
          <div className="py-4 space-y-2">
            <p>Provisional Sanction Amount:</p>
            <Input
              value={provisionalSanctionAmount}
              onChange={(e) => setProvisionalSanctionAmount(e.target.value)}
            />
            <p>Approve File</p>
            <Input
              type="file"
              id="approve_file"
              name="approve_file"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setApproveFile(e.target.files[0]);
                }
              }}
            />
            <p className="pt-2">Enter remarks for Approve:</p>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter remarks..."
            />
          </div>
          <DialogFooter className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => setAdminApproveModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApprove}
              disabled={isSubmitting || !remarks.trim()}
            >
              {isSubmitting ? "Processing..." : "Approve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CFCSubsidyDetails;
